import axios from 'axios';
import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import AdminLayout from '../Componets/AdminLayout';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-tooltip/dist/react-tooltip.css';
import { Tooltip } from 'react-tooltip';
import * as XLSX from 'xlsx'; // Import XLSX library
import { PDFDocument } from 'pdf-lib';

export default function BarcodeReport() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [overallTotals, setOverallTotals] = useState({});
  const location = useLocation();
  const { facultyName, subjectId, examId } = location.state || {};
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const role = localStorage.getItem('role');
    if (role !== 'ADMIN') {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    fetchFacultyReport();
  }, []);

  const fetchFacultyReport = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const data = { examId, subjectId, facultyName };

    try {
      const response = await axios.post(
        `${apiUrl}/api/answersheet/faculty-report`,
        data,
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
        }
      );

      // Flatten the nested barcode structure properly
      const flattenedData = response.data.data.flatMap((record) =>
        Object.entries(record.barcode || {}).flatMap(
          ([barcodeNo, detailsArray]) =>
            detailsArray.map((details) => ({
              facultyName: record.facultyName,
              barcodeNo,
              status: details.status,
              startTime: details.startDate,
              endDate: details.endDate,
              checkingTime: details.time,
              facultyId: record.facultyId,
              QRCodeId: details.QRCodeId,
              barcodeId: details.BarcodeId,
              allocationType: details.allocationType,
            }))
        )
      );

      setRecords(flattenedData);
      setOverallTotals(response.data.overallTotals || {});
      setLoading(false);
    } catch (error) {
      console.error('Error fetching faculty report:', error);
      setLoading(false);
      if (error.response?.status === 401) navigate('/');
    }
  };

  const handleExportToExcel = () => {
    // Convert records to worksheet data
    const worksheetData = records.map((record, index) => ({
      No: index + 1,
      'Allocation Type': record.allocationType,
      'Barcode No': record.barcodeNo,
      Status: record.status,
      Duration: `${record.startTime} to ${record.endDate}`, // Merging Start Date & End Date
      'Checking Time': record.checkingTime,
    }));

    // Create a new workbook and add the worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);

    // Set column widths
    worksheet['!cols'] = [
      { wch: 5 }, // No
      { wch: 20 }, // Allocation Type
      { wch: 15 }, // Barcode No
      { wch: 10 }, // Status
      { wch: 25 }, // Duration (Merged Start Date & End Date)
      { wch: 15 }, // Checking Time
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Faculty Report');

    // Trigger file download
    XLSX.writeFile(workbook, `Report_${facultyName}.xlsx`);
  };

  const handleImageClick = (row) => {
    // Encode the barcode in base64 using btoa
    const encodedBarcode = btoa(row.barcodeNo || '');
    // Navigate with the encoded barcode and other data in the state
    navigate('/ViewAnswerSheet', {
      state: {
        facultyId: row.facultyId, // Faculty ID
        id: examId, // Exam ID
        subjectId: subjectId, // Subject ID
        barcode: encodedBarcode, // Encoded barcode
        barcodeId: row.barcodeId, // QR Code ID
        allocationType: row.allocationType,
        userType: 'admin',
        StaTus: 'COMPLETED',
      },
    });
  };

  const columns = [
    {
      name: 'No',
      selector: (row, index) => {
        // Calculate serial number based on current page and rows per page
        return (currentPage - 1) * rowsPerPage + index + 1;
      },
      width: '50px',
      center: 'true',
    },
    {
      name: 'Allocton Type',
      selector: (row) => row.allocationType,
      sortable: true,
    },
    {
      name: 'Barcode No',
      selector: (row) => row.barcodeNo,
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        let statusColor = 'text-dark '; // Default color for 'Pending'
        if (row.status === 'INPROGRESS') statusColor = 'text-warning ';
        if (row.status === 'COMPLETED') statusColor = 'text-success ';
        return <span className={statusColor}>{row.status}</span>;
      },
    },
    {
      name: 'Start Date to End Date',
      selector: (row) => `${row.startTime} To ${row.endDate}`,
      sortable: true,
    },

    {
      name: 'Checking Time',
      selector: (row) => row.checkingTime,
      sortable: true,
    },
    {
      name: 'Sheet',
      width: '110px',
      center: 'true',
      cell: (row) => (
        <div>
          <button
            className="btn btn-primary btn-sm "
            onClick={() => handleImageClick(row)}
            data-tooltip-id="my-tooltip"
            data-tooltip-content="View Answer Sheet"
          >
            <i className="bi bi-journal-richtext"></i>
          </button>
          <button
            className="btn btn-primary btn-sm ms-2 "
            onClick={() => fetchData(row)}
            data-tooltip-id="my-tooltip"
            data-tooltip-content="Generate PDF"
          >
            <i className="bi bi-file-earmark-pdf-fill"></i>
          </button>
        </div>
      ),
    },
  ];

  // ganaret pdf for student Answersheet
  const fetchData = async (row) => {
    try {
      const encodedBarcode = btoa(row.barcodeNo || '');
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch Base64 Image Data
      const imageResponse = await axios.get(
        `${apiUrl}/api/answersheet/imageIndex/${examId}/${subjectId}/${encodedBarcode}`,
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
        }
      );

      // Fetch Page-wise Annotation Data
      const pageResponse = await axios.get(
        `${apiUrl}/api/answersheet/image/detail/${examId}/${subjectId}/${encodedBarcode}?allocationType=NORMAL&facultyId=${row.facultyId}`,
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
        }
      );

      const images = imageResponse.data.images;
      const pages = pageResponse.data.pages;

      await createPDF(images, pages);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };
  const createPDF = async (images, pages) => {
    const pdfDoc = await PDFDocument.create();
    const standardWidth = 595; // A4 width in points
    const standardHeight = 842; // A4 height in points

    for (const page of pages) {
      const imageEntry = images.find(
        (img) => img.imageName === Object.values(page)[0]
      );

      if (imageEntry) {
        const fullBase64Image = imageEntry.base64Image;
        const pageImage = await pdfDoc.embedJpg(fullBase64Image);
        const { width, height } = pageImage.scale(1);

        // Calculate scale factor to maintain aspect ratio
        const scaleFactor = Math.min(
          standardWidth / width,
          standardHeight / height
        );
        const newWidth = width * scaleFactor;
        const newHeight = height * scaleFactor;

        // Create a new PDF page with standard size
        const pdfPage = pdfDoc.addPage([standardWidth, standardHeight]);

        // Center image on page
        pdfPage.drawImage(pageImage, {
          x: (standardWidth - newWidth) / 2,
          y: (standardHeight - newHeight) / 2,
          width: newWidth,
          height: newHeight,
        });

        // Draw Annotations
        for (const annotation of page.imageJson) {
          try {
            const annotationPath = `/assets/img/${annotation.path}`;
            const annotationImg = await convertSVGtoPNG(annotationPath, pdfDoc);

            // Adjust annotation positions relative to new image dimensions
            const adjustedX = (annotation.x / width) * newWidth;
            const adjustedY =
              standardHeight -
              (annotation.y / height) * newHeight -
              annotation.height;

            pdfPage.drawImage(annotationImg, {
              x: adjustedX,
              y: adjustedY,
              width: annotation.width,
              height: annotation.height,
            });
          } catch (err) {
            console.error(
              `Error embedding annotation: ${annotation.path}`,
              err
            );
          }
        }
      }
    }

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Generated.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Convert SVG to PNG
  const convertSVGtoPNG = async (svgPath, pdfDoc) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = svgPath;
      img.crossOrigin = 'Anonymous';
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        canvas.toBlob(async (blob) => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = async () => {
            const base64Image = reader.result.split(',')[1];
            const embeddedImage = await pdfDoc.embedPng(base64Image);
            resolve(embeddedImage);
          };
        }, 'image/png');
      };
      img.onerror = () => reject('Error loading SVG file.');
    });
  };

  return (
    <div>
      <main id="main" className="main">
        <AdminLayout ac11="active">
          <div className="pagetitle">
            <nav>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/AdminHome">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/ReportDashbord">Report Exams</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/ReportExam" state={{ examId: examId }}>
                    Report Subjects
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link
                    to="/Report"
                    state={{ subjectId: subjectId, examId: examId }}
                  >
                    Report faculty
                  </Link>
                </li>
                <li className="breadcrumb-item active">Report Details</li>
              </ol>
            </nav>
          </div>
          <hr className="mb-0" />
          <div className="row">
            <div className="container">
              <div className="datatable-top">
                <div className="datatable-search">
                  <input
                    className="form-control"
                    type="number"
                    placeholder="Barcode No"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                  />
                </div>
                <div
                  className="datatable-actions mt-2 text-bold"
                  style={{ marginRight: '436px', fontSize: '16px' }}
                >
                  Report For <strong>{facultyName}</strong>
                </div>
              </div>
              <div className="table-responsive">
                <DataTable
                  columns={columns}
                  data={records.filter((record) =>
                    record.barcodeNo.includes(searchTerm)
                  )}
                  progressPending={loading}
                  S
                  pagination
                  className="custom-table"
                  paginationPerPage={rowsPerPage} // Set rows per page
                  paginationTotalRows={records.length} // Total number of records
                  paginationDefaultPage={currentPage} // Default starting page
                  onChangePage={(page) => setCurrentPage(page)} // Update current page
                  onChangeRowsPerPage={(newPerPage) =>
                    setRowsPerPage(newPerPage)
                  } // Update rows per page
                />
                <div className="d-flex justify-content-end mt-3">
                  <button
                    onClick={() =>
                      navigate('/Report', {
                        state: { facultyName, subjectId, examId },
                      })
                    }
                    className="btn btn-primary"
                  >
                    <i className="bi bi-arrow-bar-left"></i> Back
                  </button>
                  <button
                    onClick={handleExportToExcel}
                    className="btn btn-primary ms-2"
                  >
                    Generate Excel
                  </button>
                </div>
                <ToastContainer
                  position="top-center"
                  style={{ width: 'auto' }}
                />
              </div>
            </div>
          </div>
        </AdminLayout>
        <Tooltip id="my-tooltip" />
      </main>
    </div>
  );
}
